"use client";

import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { fetchWithAuth, API_BASE } from "@/lib/auth";

interface RaiseHandModalProps {
    poolId: string;
    gigName: string;
    description: string;
    children?: React.ReactNode;
    onSuccess?: () => void;
}

export default function RaiseHandModal({ poolId, gigName, description, children, onSuccess }: RaiseHandModalProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [step, setStep] = useState<"intro" | "profile" | "consent" | "success" | "ineligible">("intro");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
        return () => setMounted(false);
    }, []);

    // Profile State
    const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
    const [selectedSpecializations, setSelectedSpecializations] = useState<string[]>([]);
    const [selectedGeographies, setSelectedGeographies] = useState<string[]>([]);
    const [selectedFormats, setSelectedFormats] = useState<string[]>([]);
    const [consentChecked, setConsentChecked] = useState(false);

    const skillsOptions = ["Mentoring", "Technical Review", "Career Guidance", "Academic Outreach", "Presentation/Speaking"];
    const specializationOptions = ["Technical Analysis", "Quantitative Systems", "Risk Management", "Portfolio Strategy", "Behavioral Finance"];
    const geographyOptions = ["Global", "Americas", "EMEA", "Asia-Pacific", "India"];
    const formatOptions = ["Online (Video)", "Phone Call", "In-person Meeting"];

    const toggleItem = (item: string, list: string[], setList: React.Dispatch<React.SetStateAction<string[]>>) => {
        if (list.includes(item)) {
            setList(list.filter(x => x !== item));
        } else {
            setList([...list, item]);
        }
    };

    const handleOpen = () => {
        setStep("intro");
        setSelectedSkills([]);
        setSelectedSpecializations([]);
        setSelectedGeographies([]);
        setSelectedFormats([]);
        setConsentChecked(false);
        setErrorMessage(null);
        setIsOpen(true);
    };

    const handleProceedToProfile = () => {
        setStep("profile");
    };

    const handleProceedToConsent = () => {
        setStep("consent");
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setErrorMessage(null);
        try {
            const res = await fetchWithAuth(`${API_BASE}/api/v1/gigs/pools/${poolId}/raise-hand`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    availability_profile: {
                        skills: selectedSkills,
                        specializations: selectedSpecializations,
                        geographies: selectedGeographies,
                        formats: selectedFormats
                    },
                    consent: consentChecked
                })
            });

            if (!res.ok) {
                const errorData = await res.json();
                const detail = errorData.detail || "Failed to submit opt-in";
                setErrorMessage(detail);
                if (res.status === 403 || detail.toLowerCase().includes("eligible") || detail.toLowerCase().includes("membership")) {
                    setStep("ineligible");
                }
                return;
            }

            setStep("success");
            if (onSuccess) {
                onSuccess();
            }
        } catch (error: any) {
            console.error("Error opting into standing pool:", error);
            setErrorMessage(error.message || "An unexpected error occurred.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <div onClick={handleOpen} className="inline-block cursor-pointer">
                {children || (
                    <button className="bg-brand-sage text-white px-5 py-2.5 rounded-xl font-semibold shadow-lg shadow-black/10 hover:bg-[#a9c4bb]/90 transition-all duration-300 border-none cursor-pointer">
                        Opt-In to Standing Pool
                    </button>
                )}
            </div>

            {isOpen && mounted && createPortal(
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 font-sans animate-in fade-in duration-200">
                    <div className="bg-[#fcfcfc] rounded-xl shadow-2xl w-full max-w-lg overflow-hidden border border-gray-200 animate-in slide-in-from-bottom-2 duration-300 max-h-[90vh] flex flex-col">
                        
                        {/* Header: spec deep teal header */}
                        <div className="bg-[#004d57] text-white p-6 relative shrink-0">
                            <h2 className="font-sans font-black text-xl text-white">{gigName} Pool</h2>
                            <p className="text-[#c3d6d9] text-xs mt-1 font-semibold uppercase tracking-wider">Standing Availability Registration</p>
                            <button 
                                onClick={() => setIsOpen(false)}
                                className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 border-none text-white w-[28px] h-[28px] rounded-md transition-all flex items-center justify-center cursor-pointer font-bold"
                            >
                                ×
                            </button>
                        </div>

                        {/* Error Alert */}
                        {errorMessage && step !== "ineligible" && (
                            <div className="bg-rose-50 text-rose-800 p-4 border-b border-rose-100 text-xs font-semibold leading-relaxed shrink-0">
                                <strong>Error:</strong> {errorMessage}
                            </div>
                        )}

                        <div className="overflow-y-auto flex-1 bg-white">
                            {/* Step 1: Intro */}
                            {step === "intro" && (
                                <div className="p-6 space-y-5">
                                    <div>
                                        <h3 className="font-sans font-bold text-base text-[#004d57] mb-1.5">Raise Your Hand to Serve</h3>
                                        <p className="text-gray-500 text-xs sm:text-sm leading-relaxed">
                                            {description || "By joining this standing pool, you indicate your ongoing availability to take on opportunities for this gig name on-demand. Your profile will be visible to organizers seeking qualified volunteers."}
                                        </p>
                                    </div>
                                    
                                    <div className="bg-[#007a8a]/5 border border-[#007a8a]/10 rounded-lg p-4">
                                        <h4 className="text-xs font-bold uppercase tracking-wider text-[#003d45] mb-2 flex items-center gap-2">
                                            <svg className="w-4 h-4 text-[#007a8a]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                                            What does this mean?
                                        </h4>
                                        <p className="text-xs text-gray-500 leading-relaxed">
                                            {`Members who opt into the ${gigName} standby pool indicate their ongoing availability to support matching opportunities on-demand. This is a critical engine of CMT Association's professional community.`}
                                        </p>
                                    </div>

                                    <div className="flex justify-end gap-3 border-t pt-4 border-gray-100">
                                        <button 
                                            onClick={() => setIsOpen(false)}
                                            className="px-5 py-2.5 rounded-lg text-gray-500 hover:bg-gray-50 transition-colors font-bold text-xs uppercase tracking-wider border-none cursor-pointer"
                                        >
                                            Cancel
                                        </button>
                                        <button 
                                            onClick={handleProceedToProfile}
                                            className="bg-[#007a8a] text-white px-5 py-2.5 rounded-lg font-bold uppercase tracking-wider hover:bg-[#00606d] transition-all shadow-sm text-xs border-none cursor-pointer"
                                        >
                                            Proceed
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Step 2: Profile Selection */}
                            {step === "profile" && (
                                <div className="p-6 space-y-5">
                                    <h3 className="font-sans font-bold text-base text-[#004d57]">Select Your Profile & Alignment</h3>
                                    
                                    {/* Skills */}
                                    <div className="space-y-2">
                                        <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Skills & Contribution Areas</h4>
                                        <div className="flex flex-wrap gap-2">
                                            {skillsOptions.map(skill => (
                                                <button
                                                    key={skill}
                                                    type="button"
                                                    onClick={() => toggleItem(skill, selectedSkills, setSelectedSkills)}
                                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all border cursor-pointer select-none ${
                                                        selectedSkills.includes(skill)
                                                            ? "bg-[#007a8a] text-white border-[#007a8a]"
                                                            : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50 hover:text-gray-700"
                                                    }`}
                                                >
                                                    {skill}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Specializations */}
                                    <div className="space-y-2">
                                        <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Technical Specializations</h4>
                                        <div className="flex flex-wrap gap-2">
                                            {specializationOptions.map(spec => (
                                                <button
                                                    key={spec}
                                                    type="button"
                                                    onClick={() => toggleItem(spec, selectedSpecializations, setSelectedSpecializations)}
                                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all border cursor-pointer select-none ${
                                                        selectedSpecializations.includes(spec)
                                                            ? "bg-[#007a8a] text-white border-[#007a8a]"
                                                            : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50 hover:text-gray-700"
                                                    }`}
                                                >
                                                    {spec}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Formats */}
                                    <div className="space-y-2 pb-2">
                                        <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Communication Formats</h4>
                                        <div className="flex flex-wrap gap-2">
                                            {formatOptions.map(fmt => (
                                                <button
                                                    key={fmt}
                                                    type="button"
                                                    onClick={() => toggleItem(fmt, selectedFormats, setSelectedFormats)}
                                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all border cursor-pointer select-none ${
                                                        selectedFormats.includes(fmt)
                                                            ? "bg-[#007a8a] text-white border-[#007a8a]"
                                                            : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50 hover:text-gray-700"
                                                    }`}
                                                >
                                                    {fmt}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="flex justify-end gap-3 border-t pt-4 border-gray-100">
                                        <button 
                                            onClick={() => setStep("intro")}
                                            className="px-5 py-2.5 rounded-lg text-gray-500 hover:bg-gray-50 transition-colors font-bold text-xs uppercase tracking-wider border-none cursor-pointer"
                                        >
                                            Back
                                        </button>
                                        <button 
                                            onClick={handleProceedToConsent}
                                            disabled={selectedSkills.length === 0 || selectedFormats.length === 0}
                                            className="bg-[#007a8a] text-white px-5 py-2.5 rounded-lg font-bold uppercase tracking-wider hover:bg-[#00606d] transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed text-xs border-none cursor-pointer"
                                        >
                                            Proceed
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Consent */}
                            {step === "consent" && (
                                <div className="p-6 space-y-5">
                                    <h3 className="font-sans font-bold text-base text-[#004d57]">Declaration & Consent</h3>
                                    
                                    <div className="bg-[#007a8a]/5 border border-[#007a8a]/10 rounded-lg p-4 text-xs sm:text-sm text-gray-600 leading-relaxed">
                                        By submitting this standing pool registration:
                                        <ul className="list-disc pl-5 mt-2 space-y-1.5 text-xs text-gray-500">
                                            <>
                                                <li>You agree to assist with matching tasks with professional integrity and represent the values of the Association.</li>
                                                <li>You consent to your name and availability profile being surfaced to organizers and staff seeking qualified volunteers.</li>
                                            </>
                                        </ul>
                                    </div>

                                    <label className="flex items-start space-x-3 cursor-pointer select-none">
                                        <input 
                                            type="checkbox"
                                            checked={consentChecked}
                                            onChange={(e) => setConsentChecked(e.target.checked)}
                                            className="w-4 h-4 rounded border-gray-300 text-[#007a8a] focus:ring-[#007a8a] mt-0.5"
                                        />
                                        <span className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                                            {`I accept this responsibility and declare that I meet all criteria to serve in the ${gigName} pool.`}
                                        </span>
                                    </label>

                                    <div className="flex justify-end gap-3 border-t pt-4 border-gray-100">
                                        <button 
                                            onClick={() => setStep("profile")}
                                            disabled={isSubmitting}
                                            className="px-5 py-2.5 rounded-lg text-gray-500 hover:bg-gray-50 transition-colors font-bold text-xs uppercase tracking-wider border-none cursor-pointer"
                                        >
                                            Back
                                        </button>
                                        <button 
                                            onClick={handleSubmit} 
                                            disabled={isSubmitting || !consentChecked}
                                            className="bg-[#007a8a] text-white px-5 py-2.5 rounded-lg font-bold uppercase tracking-wider hover:bg-[#00606d] transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed text-xs border-none cursor-pointer"
                                        >
                                            {isSubmitting ? "Submitting..." : "Opt-In (Submit)"}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Step 4: Success */}
                            {step === "success" && (
                                <div className="p-8 text-center space-y-4">
                                    <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto border border-emerald-100">
                                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                                    </div>
                                    <h3 className="font-sans font-bold text-lg text-[#004d57]">Standing Pool Joined!</h3>
                                    <p className="text-gray-500 text-xs leading-relaxed max-w-md mx-auto">
                                        Thank you! You have successfully registered your availability for the <strong>{gigName}</strong> standing pool. An email confirmation has been sent to your account.
                                    </p>
                                    <button 
                                        onClick={() => setIsOpen(false)} 
                                        className="w-full bg-[#007a8a] text-white py-3 rounded-lg font-bold uppercase tracking-wider hover:bg-[#00606d] transition-all duration-300 text-xs border-none cursor-pointer"
                                    >
                                        Done
                                    </button>
                                </div>
                            )}

                            {/* Step 5: Ineligible */}
                            {step === "ineligible" && (
                                <div className="p-8 text-center space-y-5 bg-white">
                                    <div className="w-16 h-16 bg-amber-50 text-amber-600 rounded-full flex items-center justify-center mx-auto border border-amber-100 animate-pulse">
                                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m0-8v6m0 5h.01M5.93 19.5h12.14a2 2 0 001.73-3L13.73 5.5a2 2 0 00-3.46 0L4.2 16.5a2 2 0 001.73 3z" />
                                        </svg>
                                    </div>
                                    <div className="space-y-2">
                                        <h3 className="font-sans font-extrabold text-lg text-[#004d57]">Eligibility Requirement Not Met</h3>
                                        <p className="text-gray-500 text-xs sm:text-sm leading-relaxed max-w-sm mx-auto">
                                            We were unable to complete your registration.
                                        </p>
                                    </div>
                                    <div className="bg-amber-50/60 border border-amber-100/80 rounded-xl p-4 max-w-md mx-auto text-left">
                                        <span className="text-[10px] font-bold text-amber-800 uppercase tracking-wider block mb-1">Reason</span>
                                        <p className="text-xs text-amber-900 font-medium leading-relaxed">
                                            {errorMessage || "Active membership is required (Affiliate, Professional, or Charterholder)."}
                                        </p>
                                    </div>
                                    <button 
                                        onClick={() => setIsOpen(false)} 
                                        className="w-full bg-[#007a8a] text-white py-3 rounded-lg font-bold uppercase tracking-wider hover:bg-[#00606d] transition-all duration-300 text-xs border-none cursor-pointer"
                                    >
                                        Close
                                    </button>
                                </div>
                            )}
                        </div>

                    </div>
                </div>,
                document.body
            )}
        </>
    );
}

